import {
  Address,
  AddressUtil,
  AgpSharedReadableAddressPipe,
  AustraliaUtil,
  NewZealandUtil
} from '@liberty-financial/agp-shared';
import { GuidUtil } from '@liberty-financial/ng-core';
import * as _ from 'lodash';

export namespace SparkAddress {
  export interface LocalAddressContract {
    localAddress: {
      unitNumber: string | null;
      streetName: string;
      streetNumber: string | null;
      streetType: string | null;
      suburb: {
        name: string;
        state: string;
        postcode: string;
      };
      country: string;
      googleLookup: {
        displayText: string | null;
        latitude: number | null;
        longitude: number | null;
        apiId: string | null;
      } | null;
    };
  }

  export interface InternationalAddressContract {
    internationalAddress: {
      addressLine1: string;
      addressLine2: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
      country: string;
    };
  }

  export type SchemaContract =
    | LocalAddressContract
    | InternationalAddressContract;

  export function toSchemaContract(
    address: Address | null
  ): SchemaContract | null {
    if (!address) {
      return null;
    }

    if (AddressUtil.isInternational(address)) {
      return {
        internationalAddress: {
          addressLine1: address.addressLine1 || '',
          addressLine2: address.addressLine2 || null,
          city: address.city || '',
          state: address.state,
          zip: address.postcode,
          country: address.country
        }
      };
    }

    if (AddressUtil.isAustralia(address)) {
      return {
        localAddress: {
          unitNumber: address.unitNumber || null,
          streetNumber: address.streetNumber || null,
          streetName: address.streetName,
          streetType: address.streetType,
          googleLookup: address.geo?.placeId
            ? {
                displayText: Model.getDisplayText({
                  ...address,
                  state: AustraliaUtil.getStateInShortName(address.state) || ''
                }),
                latitude: address.geo?.lat || null,
                longitude: address.geo?.lng || null,
                apiId: address.geo?.placeId || null
              }
            : null,
          suburb: {
            name: address.suburb || '',
            state: AustraliaUtil.getStateInShortName(address.state) || '',
            postcode: address.postcode
          },
          country: address.country
        }
      };
    }

    return {
      localAddress: {
        unitNumber: address.unitNumber || null,
        streetNumber: address.streetNumber || null,
        streetName: address.streetName,
        streetType: address.streetType,
        googleLookup: address.geo?.placeId
          ? {
              displayText: Model.getDisplayText(address),
              latitude: address.geo?.lat || null,
              longitude: address.geo?.lng || null,
              apiId: address.geo?.placeId || null
            }
          : null,
        suburb: {
          name: address.suburb || '',
          state: address.city || '',
          postcode: address.postcode
        },
        country: address.country
      }
    };
  }

  function mapNewZealandCityWithUpperCaseCheck(cityText: string): string {
    if (cityText === cityText.toUpperCase()) {
      let found = NewZealandUtil.cities.find((city) => city.toUpperCase() === cityText.toUpperCase());
      return found ?? cityText;
    }

    return cityText;
  }

  export function toModel(address?: SchemaContract | null): Address | null {
    if (!address) {
      return null;
    }

    const id = GuidUtil.generate();

    if ('internationalAddress' in address) {
      return {
        id,
        addressLine1: address.internationalAddress.addressLine1 || '',
        addressLine2: address.internationalAddress.addressLine2 || undefined,
        city: address.internationalAddress.city || '',
        state: address.internationalAddress.state || '',
        postcode: address.internationalAddress.zip || '',
        country: address.internationalAddress.country
      };
    }
    const country = address.localAddress.country;

    if (country === 'Australia') {
      return {
        id,
        unitNumber: address.localAddress.unitNumber || undefined,
        streetNumber: address.localAddress.streetNumber || undefined,
        streetName: address.localAddress.streetName,
        streetType: address.localAddress.streetType || '',
        geo: address.localAddress.googleLookup?.apiId
          ? {
              lat: address.localAddress.googleLookup?.latitude || 0,
              lng: address.localAddress.googleLookup?.longitude || 0,
              placeId: address.localAddress.googleLookup?.apiId || ''
            }
          : undefined,
        suburb: address.localAddress.suburb.name || '',
        state:
          AustraliaUtil.getStateInLongName(address.localAddress.suburb.state) ||
          '',
        postcode: address.localAddress.suburb.postcode,
        country: 'Australia'
      };
    }

    return {
      id,
      unitNumber: address.localAddress.unitNumber || undefined,
      streetNumber: address.localAddress.streetNumber || undefined,
      streetName: address.localAddress.streetName,
      streetType: address.localAddress.streetType || '',
      geo: address.localAddress.googleLookup?.apiId
        ? {
            lat: address.localAddress.googleLookup?.latitude || 0,
            lng: address.localAddress.googleLookup?.longitude || 0,
            placeId: address.localAddress.googleLookup?.apiId || ''
          }
        : undefined,
      suburb: address.localAddress.suburb.name || '',
      city: mapNewZealandCityWithUpperCaseCheck(address.localAddress.suburb.state),
      postcode: address.localAddress.suburb.postcode,
      country: 'New Zealand'
    };
  }

  export namespace Model {
    export function getDisplayText(address?: Address | null) {
      return new AgpSharedReadableAddressPipe().transform(address);
    }
  }
}
